import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { reportError, BUILD_ID } from "@/lib/errorReporter";

const RELOAD_FLAG = "chunk-reload-attempt";

/** Erros de carregamento de chunk acontecem quando o navegador tem uma versão antiga em cache. */
function isChunkLoadError(error: unknown) {
  const msg = error instanceof Error ? `${error.name} ${error.message}` : String(error);
  return /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed|dynamically imported module|ChunkLoadError|Loading CSS chunk/i.test(
    msg,
  );
}

function reloadOnce() {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return false;
    sessionStorage.setItem(RELOAD_FLAG, "1");
  } catch {
    /* noop */
  }
  window.location.reload();
  return true;
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    reportError(error, {
      source: "react-error-boundary",
      componentStack: errorInfo.componentStack ?? undefined,
    });
    if (isChunkLoadError(error)) reloadOnce();
  }

  handleReset = () => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      /* noop */
    }
    if (isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="min-h-[50vh] flex flex-col items-center justify-center p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center mb-6">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
          <h2 className="text-xl font-display text-foreground mb-2">Algo deu errado</h2>
          <p className="text-muted-foreground text-sm mb-6 max-w-md">
            Ocorreu um erro inesperado. Tente recarregar a página ou voltar para a tela inicial.
          </p>
          <p className="mb-6 font-mono text-[11px] text-muted-foreground/70">
            build {BUILD_ID}
          </p>
          <div className="flex gap-3">
            <button
              onClick={this.handleReset}
              className="btn-elegant flex items-center gap-2 text-sm"
            >
              <RefreshCw className="w-4 h-4" /> Tentar novamente
            </button>
            <a href="/" className="btn-ghost-elegant flex items-center gap-2 text-sm px-4 py-2">
              Ir para o início
            </a>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
